import type { components } from "@dolfin/api-admin";
import { lineBucketValues, matchLineStateFilterValues, matchGroupStatusValues } from "@dolfin/api-admin";

// Shared matching vocabulary. Where a label names a generated enum value it IS that value, humanized, never reworded.

type Allocation = components["schemas"]["MatchAllocationAdminView"];
export type MatchAutoVerdict = components["schemas"]["MatchAutoVerdict"];
export type LineBucket = components["schemas"]["LineBucket"];
export type MatchGroupStatus = components["schemas"]["MatchGroupStatus"];

export type BadgeVariant = "default" | "secondary" | "outline" | "destructive";

// The MatchMethod value verbatim, not humanized: staff search for it in the spec, backend and logs, and new values
// name themselves. autoAccepted is appended because Fuzzy covers both engine auto-accept and a reviewer confirming it.
export function methodBadge(a: Pick<Allocation, "matchMethod" | "autoAccepted">): {
  label: string;
  variant: BadgeVariant;
} {
  return {
    label: a.autoAccepted ? `${a.matchMethod} · auto` : a.matchMethod,
    // Only a hand-made match is emphasised: it's the one worth spotting in a long list.
    variant: a.matchMethod === "Manual" ? "default" : "secondary",
  };
}

// Only Fuzzy matches record a score; Exact (SKU-equal) is certain, so it shows 100% rather than an empty pill.
export function allocationConfidence(
  a: Pick<Allocation, "matchMethod" | "matchConfidence">,
): number | string | null {
  if (a.matchConfidence !== null && a.matchConfidence !== undefined) return a.matchConfidence;
  return a.matchMethod === "Exact" ? 1 : null;
}

// Each hint heads off a misreading: Ambiguous is the margin, not the threshold; NoCandidate means no document to match.
const VERDICTS: Record<
  NonNullable<MatchAutoVerdict>,
  { label: string; hint: string; variant: BadgeVariant }
> = {
  Allocated: {
    label: "Nothing left to decide",
    hint: "Every dimension the engine fills already has a counterpart.",
    variant: "secondary",
  },
  NoSupplier: {
    label: "No supplier",
    hint: "The bill had no supplier resolved, so no candidate pool existed. Fix extraction, not the thresholds.",
    variant: "destructive",
  },
  RouteDisabled: {
    label: "Auto-match off",
    hint: "Auto-matching was switched off, so the engine was never allowed to try.",
    variant: "outline",
  },
  NoCandidate: {
    label: "Nothing to match",
    hint: "Nothing cleared the similarity threshold. Usually a missing document, not a matching failure.",
    variant: "outline",
  },
  BelowThreshold: {
    label: "Under the threshold",
    hint: "The engine found the right sort of thing but scored it below the auto-accept threshold.",
    variant: "destructive",
  },
  CounterpartTooLarge: {
    label: "Counterpart too large",
    hint: "A good match on name, declined because allocation is whole-or-nothing: taking all of it would overshoot the billed quantity by more than the shortfall it was closing. A reviewer can still take it.",
    variant: "outline",
  },
  Ambiguous: {
    label: "Too close to call",
    hint: "Cleared the threshold, but the runner-up was inside the margin. Lowering the threshold will not recover these.",
    variant: "destructive",
  },
  Eligible: {
    label: "Should have matched",
    hint: "Cleared every bar and still did not hold — the counterpart's quantity went elsewhere, or matching has not re-run.",
    variant: "destructive",
  },
};

export function verdictBadge(verdict: MatchAutoVerdict | null | undefined) {
  return verdict ? VERDICTS[verdict] : null;
}

// Generated numeric fields are `number | string`; one coercion helper instead of Number() everywhere.
export function num(value: number | string | null | undefined): number {
  return value === null || value === undefined ? 0 : Number(value);
}

// Null renders as nothing, not 0%: unscored and scored-zero mean opposite things.
export function formatConfidence(value: number | string | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  return `${(Number(value) * 100).toFixed(0)}%`;
}

// From the spec; the Record below is exhaustive, so a new backend bucket is a compile error until it has a gloss.
export type MatchLineStateFilter = components["schemas"]["MatchLineStateFilter"];

// Tabs use the enum value verbatim (staff read these names in the API and logs); the gloss is shown beside, never instead.
export const MATCH_LINE_STATE_FILTERS: Record<MatchLineStateFilter, string> = {
  Actionable: "Unmatched, and the supplier still has order or delivery capacity to match against.",
  Starved: "Unmatched, with no capacity left for this supplier - nothing to match to.",
  Matched: "Carries at least one allocation.",
  Accepted: "Closed with no counterpart, accepted as billed.",
  Disputed: "Raised with the supplier.",
  All: "Every line in a match group, whatever its state.",
};

// "AcceptedNoMatch" -> "Accepted no match".
export function humanizeEnumValue(value: string): string {
  const spaced = value.replace(/([a-z0-9])([A-Z])/g, "$1 $2");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

// Every value of each generated enum, in API order, so tabs and legends can't drift from the server.
export const LINE_BUCKETS = lineBucketValues;
export const MATCH_LINE_STATE_FILTER_VALUES = matchLineStateFilterValues;
export const MATCH_GROUP_STATUSES = matchGroupStatusValues;

// Exhaustive over the enum: a new server bucket stops the build until it has a gloss.
export const MATCH_LINE_STATES: Record<LineBucket, string> = {
  AutoExact: "Matched automatically on SKU.",
  AutoFuzzy: "Matched automatically on description similarity.",
  Assisted: "A reviewer confirmed a suggestion the engine had offered.",
  Manual: "A reviewer matched it by hand, taking no suggestion.",
  AcceptedNoMatch: "Closed with no counterpart, accepted as billed.",
  Disputed: "Raised with the supplier.",
  CandidateWaiting: "Unmatched, and there is still capacity to match against.",
  NoCandidate: "Unmatched, with no capacity left to match against.",
};
